import * as React from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "../Spinner";
import { ButtonVariantProps, buttonVariants } from "./button-variants";

export type ButtonProps = Omit<ButtonVariantProps, "prefix" | "suffix"> &
  Omit<React.ComponentPropsWithRef<"button">, "prefix" | "suffix"> &
  Omit<React.ComponentPropsWithRef<"a">, "prefix" | "suffix"> & {
    children?: React.ReactNode;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    ariaLabel?: string;
    testId?: string;
    className?: string;
    width?: string;
    truncate?: boolean;
    href?: string;
    shape?: "default" | "pill" | "circle";
  };

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant = "default",
      appearance = "black",
      size = "default",
      children,
      showSpinner,
      prefix,
      suffix,
      disabled,
      tabIndex,
      iconOnly,
      ariaLabel,
      testId = "ps-button",
      fullWidth,
      width,
      truncate,
      alignment = "center",
      shape = "default",
      href,
      ...props
    },
    ref,
  ) => {
    // Pill/circle default to outline on white; ghost keeps caller variant/appearance.
    const shapeForcesOutline = shape !== "default" && variant !== "ghost";
    const effectiveAppearance = shapeForcesOutline ? "white" : appearance;
    const effectiveVariant = shapeForcesOutline ? "outline-1" : variant;

    const getSpinnerAppearance = () => {
      if (
        appearance === "white" &&
        ["default", "outline-1", "outline-2"].includes(variant!)
      ) {
        return "black";
      }

      if (appearance === "black" && ["ghost", "text-only"].includes(variant!)) {
        return "black";
      }

      if (
        appearance === "purple" &&
        ["ghost", "text-only"].includes(variant!)
      ) {
        return "purple";
      }

      return "white";
    };

    const Component = href ? "a" : "button";

    return (
      <Component
        // @ts-ignore
        ref={ref}
        type="button"
        className={cn(
          "focus:tw-outline",
          buttonVariants({
            variant: effectiveVariant,
            appearance: effectiveAppearance,
            size,
            disabled: !!disabled,
            prefix: !!prefix,
            suffix: !!suffix,
            iconOnly: !!iconOnly,
            showSpinner: !!showSpinner,
            fullWidth: !!fullWidth,
            shape,
            alignment,
            className,
          }),
        )}
        style={width ? { width } : undefined}
        tabIndex={disabled || showSpinner ? -1 : tabIndex}
        aria-disabled={disabled || showSpinner ? true : undefined}
        aria-label={
          ariaLabel || (typeof children === "string" ? children : undefined)
        }
        data-testid={testId}
        href={href}
        {...props}
      >
        {showSpinner && (
          <span className="tw-absolute tw-inset-0 tw-inline-flex tw-items-center tw-justify-center">
            <Spinner
              size={size!}
              appearance={getSpinnerAppearance()}
              testId={`${testId}-spinner`}
            />
          </span>
        )}
        <span
          className={cn("tw-flex tw-items-center", {
            "tw-opacity-0": showSpinner,
            "tw-truncate": truncate,
          })}
        >
          {!!prefix && (
            <span
              data-testid={`${testId}-prefix`}
              className={cn("tw-flex tw-items-center tw-size-20 tw-mr-8", {
                "tw-size-24": size === "large",
              })}
            >
              {prefix}
            </span>
          )}

          {children}

          {!!suffix && (
            <span
              data-testid={`${testId}-suffix`}
              className={cn("tw-flex tw-items-center tw-size-20 tw-ml-8", {
                "tw-size-24": size === "large",
              })}
            >
              {suffix}
            </span>
          )}
        </span>
      </Component>
    );
  },
);

Button.displayName = "Button";
